import { LyVariantChangedEventListener } from "./base"
import * as THREE from 'three'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { RectAreaLightUniformsLib } from 'three/addons/lights/RectAreaLightUniformsLib.js'
// import { addLinkPointCameraPositionCoordinate } from './link_point_camera_position'

const DRACO_DECODER_PATH = 'https://cdn.jsdelivr.net/npm/three@0.183.2/examples/jsm/libs/draco/'

if (!customElements.get('ly-main-product-model-viewer')) {

    class LyMainProductModelViewerConfigInitializer extends LyVariantChangedEventListener {

        _allModelConfigMap = null
        _modelConfigTreeCache = new Map()

        _cameraPositions = null

        connectedCallback() {
            super.connectedCallback()
        }

        disconnectedCallback() {
            this._modelConfigTreeCache.clear()

            super.disconnectedCallback()
        }

        _transformVariantConfigs = data => {
            this._allModelConfigMap = data.allModelConfigMap || {}
            this._cameraPositions = data.cameraPositions || []

            this._modelConfigTreeCache.clear()

            return data.variantConfigs || {}
        }

        /**
         * 根据 variantId 获取完整配置树
         * @param {string|number} variantId
         * @returns {object|null}
         */
        getVariantConfig(variantId) {
            if (!this._variantConfigs) {
                console.warn('Variant 配置未初始化')
                return null
            }

            const { handle } = this._variantConfigs[variantId]

            if (!handle) {
                console.warn(`未找到 Variant 配置：${variantId}`)
                return null
            }

            return this.resolveModelConfig(handle)
        }

        /**
         * 递归解析 model config tree
         * 自动将 model_config_handles
         * 转换成完整 model_configs
         *
         * @param {string} handle
         * @param {Set<string>} visited
         * @returns {object|null}
         */
        resolveModelConfig(handle, visited = new Set()) {
            if (!handle) {
                return null
            }

            // 防止循环引用
            if (visited.has(handle)) {
                console.warn(`检测到循环引用：${handle}`)
                return null
            }

            // 优先走缓存
            if (this._modelConfigTreeCache.has(handle)) {
                return this._modelConfigTreeCache.get(handle)
            }

            if (!this._allModelConfigMap) {
                console.warn('Model Config Map 未初始化')
                return null
            }

            const config = this._allModelConfigMap[handle]

            if (!config) {
                console.warn(`未找到 Model Config：${handle}`)
                return null
            }

            visited.add(handle)

            const resolved = {
                ...config,

                link_points: (config.link_points || []).map(point => ({
                    ...point,
                    model_configs: (point.model_config_handles || []).map(childHandle =>
                        this.resolveModelConfig(childHandle, new Set(visited))
                    ).filter(Boolean)
                }))
            }

            visited.delete(handle)

            this._modelConfigTreeCache.set(handle, resolved)

            return resolved
        }
    }

    class LyMainProductModelViewerRenderConfigInitializer extends LyMainProductModelViewerConfigInitializer {

        #resizeObserver = null
        /** 渲染器尺寸 */
        get #rendererSize() {
            const rect = this.getBoundingClientRect()
            return [rect.width, rect.height]
        }
        /** 模型loader */
        #gltfLoader = new GLTFLoader()

        /** 渲染器 */
        #renderer = null
        /** 场景 */
        #scene = null
        /** 模型root组 */
        _rootGroup = null
        /** 相机 */
        #camera = null
        /** 相机初始位置 */
        #cameraInitPos = [0, 0, 30]
        /** 轨道控制器 */
        _orbit = null

        /** 是否正在渲染 */
        _isRendering = false
        /** 是否需要渲染一次 */
        _needRenderOnce = true
        /** 渲染请求队列 */
        #renderRequests = new Set()
        /** 是否需要渲染 */
        get #needRenderFrame() {
            return this._needRenderOnce || this.#renderRequests.size > 0
        }

        _requestRender = (key) => {
            if (this.#renderRequests.has(key)) return
            this.#renderRequests.add(key)
        }

        _cancelRender = (key) => {
            this.#renderRequests.delete(key)
        }

        #animationMixers = []
        #clock = new THREE.Clock()

        constructor() {
            super()

            const dracoLoader = new DRACOLoader()
            // 使用官方 CDN 解码器
            dracoLoader.setDecoderPath(DRACO_DECODER_PATH)
            // 绑定到 #gltfLoader
            this.#gltfLoader.setDRACOLoader(dracoLoader)
        }

        connectedCallback() {
            super.connectedCallback()

            this.#resizeObserver = new ResizeObserver(this._resizeHandler)
            this.#resizeObserver.observe(this)
        }

        disconnectedCallback() {
            this._stopRenderLoop()

            this.#resizeObserver.disconnect()
            this.#resizeObserver = null

            this._orbit?.dispose()
            this._orbit?.removeEventListener('change', this._registerOrbitRenderRequest)
            this.#renderer?.dispose()
            this.#renderer?.domElement.remove()

            this.#scene?.traverse(obj => this._disposeObject3D(obj))
            this.#scene?.clear()

            this.#renderer = null
            this.#scene = null
            this.#camera = null
            this._rootGroup = null
            this._orbit = null

            this.#animationMixers.length = 0

            THREE.Cache.clear()

            super.disconnectedCallback()
        }

        /** 初始化入口 */
        async _initBase() {
            this._initRenderer()
            this._initScene()
            this._initMainGroup()
            this._initCamera()
            this._initLight()

            this._initOrbitController()
            this._startRenderLoop()
        }

        /** 初始化渲染器 */
        _initRenderer = () => {
            // 创建一个 WebGL 渲染器，并开启抗锯齿，让边缘更平滑
            const renderer = new THREE.WebGLRenderer({ antialias: true })

            // 设置渲染画布的宽高（通常与容器或窗口大小一致）
            renderer.setSize(...this.#rendererSize)

            // 设置像素比，使高分辨率屏幕（如 Retina）下画面更清晰
            renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

            // 设置渲染输出的颜色空间为 sRGB，使颜色显示更准确、更接近人眼感知
            renderer.outputColorSpace = THREE.SRGBColorSpace

            // 使用 ACES Filmic 色调映射，使高光和暗部过渡更自然，整体观感更电影化
            renderer.toneMapping = THREE.ACESFilmicToneMapping

            // 设置曝光度，避免画面过暗或过亮
            renderer.toneMappingExposure = 1

            // 启用物理正确的光照计算，使光照衰减和反射更符合真实物理规律
            renderer.physicallyCorrectLights = true

            this.appendChild(renderer.domElement)
            this.#renderer = renderer
        }

        /** 初始化场景 */
        _initScene = () => {
            const scene = new THREE.Scene()
            scene.background = new THREE.Color(0xfafafa)
            // scene.fog = new THREE.Fog(0xf0f0f0, 20, 40)
            this.#scene = scene
        }

        /** 初始化主容器 */
        _initMainGroup = () => {
            const group = new THREE.Group()

            // 劫持 add
            const originalAdd = group.add
            group.add = (...args) => {
                const result = originalAdd.apply(group, args)
                this._needRenderOnce = true
                return result
            }

            // 劫持 remove
            const originalRemove = group.remove
            group.remove = (...args) => {
                const result = originalRemove.apply(group, args)
                this._needRenderOnce = true
                return result
            }

            this.#scene.add(group)
            this._rootGroup = group
        }

        /** 初始化相机 */
        _initCamera = () => {
            const fov = 20
            const aspect = this.#rendererSize[0] / this.#rendererSize[1]
            const near = 0.1
            const far = 2000

            const camera = new THREE.PerspectiveCamera(fov, aspect, near, far)
            camera.position.set(...this.#cameraInitPos)
            camera.lookAt(0, 0, 0)

            this.#camera = camera
        }

        /** 初始化光线（可在子类中重写） */
        _initLight = () => {
            // 环境光（极弱）
            const ambient = new THREE.AmbientLight(0xffffff, 0.05)
            this.#scene.add(ambient)

            // 主光（关键光）
            const key = new THREE.DirectionalLight(0xffffff, 2.2)
            key.position.set(5, 10, 7)
            this.#scene.add(key)

            // 补光（弱）
            const fill = new THREE.DirectionalLight(0xffffff, 0.3)
            fill.position.set(-5, 3, 5)
            this.#scene.add(fill)

            // 轮廓光（增强边缘）
            const back = new THREE.DirectionalLight(0xffffff, 1.2)
            back.position.set(-5, 8, -8)
            this.#scene.add(back)

            RectAreaLightUniformsLib.init()
            // 👇 底部补光（核心）
            const bottomLight = new THREE.RectAreaLight(0xffffff, 0.5, 20, 20)

            // 放在模型下方
            bottomLight.position.set(0, -5, 0)

            // 朝上照
            bottomLight.rotation.x = Math.PI / 2

            this.#scene.add(bottomLight)
        }

        /** 初始化轨道控制器（子类可禁用或扩展） */
        _initOrbitController = () => {
            const orbit = new OrbitControls(this.#camera, this.#renderer.domElement)
            orbit.minPolarAngle = 0 // 限制相机最小仰角（0表示正上方）
            orbit.maxPolarAngle = Math.PI / 2 + 0.01 // 限制相机最大仰角，保持一个小的仰角
            orbit.enableDamping = true // 启用阻尼效果使相机移动更平滑
            orbit.dampingFactor = 0.05 // 设置阻尼系数
            orbit.enableZoom = true
            orbit.minDistance = 1
            orbit.maxDistance = 100
            orbit.enablePan = false

            this._orbit = orbit

            this._orbit.addEventListener('change', this._registerOrbitRenderRequest)
        }

        /** 注册轨道控制器渲染请求 */
        _registerOrbitRenderRequest = () => {
            this._requestRender('orbit')
        }

        /** 注销轨道控制器渲染请求 */
        _cancelOrbitRenderRequest = () => {
            this._cancelRender('orbit')
        }

        /** 开始渲染循环 */
        _startRenderLoop() {
            if (this._isRendering) return
            this._isRendering = true
            const loop = () => {
                if (!this._isRendering) return

                this._orbit?.update()

                this.#needRenderFrame && this._render()

                requestAnimationFrame(loop)
            }
            loop()
        }

        /** 停止渲染循环 */
        _stopRenderLoop() {
            this._isRendering = false
        }

        _render = () => {
            const delta = this.#clock.getDelta()

            for (const mixer of this.#animationMixers) {
                mixer.update(delta)
            }

            this._beforeRender(delta)

            this.#renderer?.render(this.#scene, this.#camera)

            this._afterRender(delta)

            this._needRenderOnce = false

            // console.log('rendering...')

            if (!this._isOrbitDampingActive) {
                this._cancelOrbitRenderRequest()
            }
        }

        #orbitLastCameraPosition = new THREE.Vector3()
        #orbitLastTarget = new THREE.Vector3()
        /** orbit是否还在damping */
        get _isOrbitDampingActive() {
            const orbit = this._orbit
            if (!orbit) return false

            const EPS = 1e-5

            const position = orbit.object.position
            const target = orbit.target

            const moved =
                position.distanceToSquared(this.#orbitLastCameraPosition) > EPS ||
                target.distanceToSquared(this.#orbitLastTarget) > EPS

            // 更新缓存
            this.#orbitLastCameraPosition.copy(position)
            this.#orbitLastTarget.copy(target)

            return moved
        }

        /** 钩子：渲染前（可由子类覆写） */
        _beforeRender = () => { }

        /** 钩子：渲染后（可由子类覆写） */
        _afterRender = () => { }

        /** 当窗口大小变化时更新 */
        _resizeHandler = () => {
            if (!this.#renderer || !this.#camera) return

            // 更新渲染器尺寸
            this.#renderer.setSize(...this.#rendererSize)

            // 更新相机宽高比
            this.#camera.aspect = this.#rendererSize[0] / this.#rendererSize[1]
            this.#camera.updateProjectionMatrix()

            this._needRenderOnce = true
        }

        _getImageViewHiddenModels = () => Object.values(this._gltfMap || {})
            .filter(item => item.type === 'yard' && item.gltf?.scene)
            .map(item => ({ object: item.gltf.scene, visible: item.gltf.scene.visible }))

        _createImageView = () => {
            if (!this.#renderer || !this.#scene || !this._rootGroup || this._loadingCount > 0) {
                throw new Error('Please wait for the model to finish loading before exporting its preview.')
            }

            this._rootGroup.updateWorldMatrix(true, true)
            const bounds = new THREE.Box3()
            const corners = []
            this._rootGroup.traverseVisible(object => {
                if (!object.isMesh) return
                const box = new THREE.Box3().setFromObject(object, true)
                if (box.isEmpty()) return
                bounds.union(box)
                for (const x of [box.min.x, box.max.x]) {
                    for (const y of [box.min.y, box.max.y]) {
                        for (const z of [box.min.z, box.max.z]) corners.push(new THREE.Vector3(x, y, z))
                    }
                }
            })
            if (bounds.isEmpty()) throw new Error('There is no model available to export.')

            const width = this.#renderer.domElement.width
            const height = this.#renderer.domElement.height
            if (!width || !height) throw new Error('The model preview canvas is not ready.')
            const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100)
            const sphere = bounds.getBoundingSphere(new THREE.Sphere())
            const verticalFov = THREE.MathUtils.degToRad(camera.fov)
            const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * camera.aspect)
            const distance = Math.max(sphere.radius / Math.sin(verticalFov / 2), sphere.radius / Math.sin(horizontalFov / 2)) * 1.35
            camera.position.copy(sphere.center).addScaledVector(new THREE.Vector3(-1, 0, 2).normalize(), distance)
            camera.position.y = bounds.min.y + (bounds.max.y - bounds.min.y) * 0.22
            camera.lookAt(sphere.center)
            camera.near = Math.max(distance / 100, 0.05)
            camera.far = Math.max(distance * 20, 100)
            camera.updateProjectionMatrix()
            camera.updateMatrixWorld(true)
            const projectBounds = () => {
                const box = new THREE.Box2()
                corners.forEach(corner => {
                    const point = corner.clone().project(camera)
                    box.expandByPoint(new THREE.Vector2(point.x, point.y))
                })
                return box
            }
            const projected = projectBounds()
            camera.zoom = 0.9 / Math.max(Math.abs(projected.min.x), Math.abs(projected.max.x), Math.abs(projected.min.y), Math.abs(projected.max.y), 1e-6)
            camera.updateProjectionMatrix()
            const projectedCrop = projectBounds()
            const left = (projectedCrop.min.x + 1) * width / 2
            const right = (projectedCrop.max.x + 1) * width / 2
            const top = (1 - projectedCrop.max.y) * height / 2
            const bottom = (1 - projectedCrop.min.y) * height / 2
            const paddingX = (right - left) * 0.05
            const paddingY = (bottom - top) * 0.05
            const x = Math.max(0, Math.floor(left - paddingX))
            const y = Math.max(0, Math.floor(top - paddingY))
            const cropWidth = Math.max(1, Math.min(width, Math.ceil(right + paddingX)) - x)
            const cropHeight = Math.max(1, Math.min(height, Math.ceil(bottom + paddingY)) - y)
            return {
                camera,
                crop: { x, y, width: cropWidth, height: cropHeight },
                state: {
                    schemaVersion: 1,
                    camera: {
                        position: camera.position.toArray(),
                        quaternion: camera.quaternion.toArray(),
                        target: sphere.center.toArray(),
                        up: camera.up.toArray(),
                        fov: camera.fov,
                        zoom: camera.zoom,
                        near: camera.near,
                        far: camera.far,
                        aspect: camera.aspect
                    },
                    crop: {
                        x: x / width,
                        y: y / height,
                        width: cropWidth / width,
                        height: cropHeight / height
                    },
                    sourceSize: { width, height },
                    outputAspect: cropWidth / cropHeight
                }
            }
        }

        getImageViewState = async () => {
            await Promise.all([this._readyPromise, this._initBasePromise])
            const hidden = this._getImageViewHiddenModels()
            try {
                hidden.forEach(({ object }) => { object.visible = false })
                return this._createImageView().state
            } finally {
                hidden.forEach(({ object, visible }) => { object.visible = visible })
            }
        }

        exportImage = async () => {
            await Promise.all([this._readyPromise, this._initBasePromise])
            const scene = this.#scene
            const hidden = this._getImageViewHiddenModels()
            const background = scene?.background
            const canvas = document.createElement('canvas')
            let renderer
            try {
                hidden.forEach(({ object }) => { object.visible = false })
                const imageView = this._createImageView()
                canvas.width = imageView.crop.width
                canvas.height = imageView.crop.height
                const context = canvas.getContext('2d')
                if (!context) throw new Error('Unable to create the preview canvas.')

                renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
                renderer.setSize(imageView.state.sourceSize.width, imageView.state.sourceSize.height, false)
                renderer.outputColorSpace = this.#renderer.outputColorSpace
                renderer.toneMapping = this.#renderer.toneMapping
                renderer.toneMappingExposure = this.#renderer.toneMappingExposure
                renderer.setClearColor(0x000000, 0)
                scene.background = null
                renderer.render(scene, imageView.camera)
                context.drawImage(renderer.domElement,
                    imageView.crop.x, imageView.crop.y, imageView.crop.width, imageView.crop.height,
                    0, 0, imageView.crop.width, imageView.crop.height)
            } finally {
                hidden.forEach(({ object, visible }) => { object.visible = visible })
                if (scene) scene.background = background
                renderer?.dispose()
                renderer?.forceContextLoss()
            }
            return new Promise((resolve, reject) => {
                canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Unable to encode the preview PNG.')), 'image/png')
            })
        }

        /** 释放单个对象的几何体与贴图 */
        _disposeObject3D = (object3D) => {
            if (!object3D) return
            object3D.traverse(child => {
                if (child.isMesh) {
                    child.geometry?.dispose()
                    const materials = Array.isArray(child.material)
                        ? child.material
                        : [child.material]
                    for (const mat of materials) {
                        if (!mat) continue
                        for (const key of Object.keys(mat)) {
                            const value = mat[key]
                            if (value && value.isTexture) value.dispose()
                        }
                        mat.dispose()
                    }
                }
            })
        }

        /** 移动相机到目标位置 */
        _moveCameraTo = (targetX, targetY, targetZ) => {
            const controls = this._orbit
            const camera = controls.object
            const target = controls.target

            // 禁用用户交互
            controls.enabled = false

            // 当前相机到目标点的向量
            const startOffset = camera.position.clone().sub(target)
            const startSpherical = new THREE.Spherical().setFromVector3(startOffset)

            // 目标位置对应的球坐标（相对于 target）
            const targetPos = new THREE.Vector3(targetX, targetY, targetZ)
            const targetOffset = targetPos.clone().sub(target)
            const targetSpherical = new THREE.Spherical().setFromVector3(targetOffset)

            const duration = 1000
            const startTime = performance.now()

            this._requestRender('camera')
            const animate = (now) => {
                const easeOutCubic = t => 1 - Math.pow(1 - t, 3)
                const t = easeOutCubic(Math.min((now - startTime) / duration, 1))

                // 球面插值
                const currentSpherical = new THREE.Spherical(
                    THREE.MathUtils.lerp(startSpherical.radius, targetSpherical.radius, t),
                    THREE.MathUtils.lerp(startSpherical.phi, targetSpherical.phi, t),
                    THREE.MathUtils.lerp(startSpherical.theta, targetSpherical.theta, t)
                )

                const newPos = new THREE.Vector3().setFromSpherical(currentSpherical).add(target)
                camera.position.copy(newPos)
                camera.lookAt(target)
                controls.update()

                if (t < 1) {
                    requestAnimationFrame(animate)
                } else {
                    controls.enabled = true
                    this._cancelRender('camera')
                }
            }

            requestAnimationFrame(animate)
        }

        /** 角度转为弧度 */
        _degToRad = val => THREE.MathUtils.degToRad(val)

        /** 加载 gltf 模型 */
        _loadGltf = (url) => {
            return new Promise((resolve, reject) => {
                this.#gltfLoader.load(url, resolve, undefined, reject)
            })
        }

        /** 获取3D对象的盒模型 */
        _getBox = obj => {
            const box = new THREE.Box3().setFromObject(obj)
            return box
        }

        /** 获取3D对象的中心点 */
        _getCenter = obj => {
            const box = this._getBox(obj)
            const center = new THREE.Vector3()
            box.getCenter(center)
            return center
        }

        /** 获取3D对象的尺寸 */
        _getSize = obj => {
            const box = this._getBox(obj)
            const size = new THREE.Vector3()
            box.getSize(size)
            return size
        }

        /** 开启坐标轴 */
        _showAxis = () => {
            const axisHelper = new THREE.AxesHelper(1000)
            this.#scene.add(axisHelper)
        }

        /*** 创建一个动画控制器，用于统一管理 glTF 中的 AnimationClip */
        _createAnimationController = (gltf) => {

            const { scene: model, animations } = gltf

            const mixer = new THREE.AnimationMixer(model)

            this.#animationMixers.push(mixer)

            const actions = {}

            animations.forEach((clip) => {
                actions[clip.name] = mixer.clipAction(clip)
            })

            const _getAction = (name) => {
                const action = actions[name]

                if (!action) {
                    throw new Error(`动画不存在: ${name}`)
                }

                return action
            }

            const _getDuration = (action) => {
                return action.getClip().duration
            }

            const _waitActionFinish = (action) => {
                return new Promise(resolve => {
                    const handler = (e) => {
                        if (e.action === action) {
                            mixer.removeEventListener('finished', handler)
                            resolve()
                        }

                    }
                    mixer.addEventListener('finished', handler)
                })
            }

            const forward = async (name = 'All Animations', options = {}) => {

                const action = _getAction(name)

                const { loop = THREE.LoopOnce, clamp = true } = options

                action.setLoop(loop)
                action.clampWhenFinished = clamp

                action.paused = false

                action.timeScale = Math.abs(action.timeScale || 1)

                this._requestRender(`mixer_forward_${model.uuid}`)

                action.play()

                await _waitActionFinish(action)

                this._cancelRender(`mixer_forward_${model.uuid}`)
                this._needRenderOnce = true
            }

            const reverse = async (name = 'All Animations', options = {}) => {

                const action = _getAction(name)

                const { loop = THREE.LoopOnce, clamp = true } = options

                const duration = _getDuration(action)

                /**
                 * 如果当前在第一帧
                 * 反向播放需要从最后一帧开始
                 */
                if (action.time === 0) {
                    action.time = duration
                }

                action.setLoop(loop)
                action.clampWhenFinished = clamp

                action.paused = false

                action.timeScale = -Math.abs(action.timeScale || 1)

                this._requestRender(`mixer_reverse_${model.uuid}`)

                action.play()

                await _waitActionFinish(action)

                this._cancelRender(`mixer_reverse_${model.uuid}`)
                this._needRenderOnce = true
            }

            const setProgress = (name = 'All Animations', progress) => {

                const action = _getAction(name)

                // 暂停动画
                action.paused = true

                // 必须 play 才能设置 time
                action.play()

                const duration = _getDuration(action)

                // 根据比例设置时间
                action.time = duration * progress
            }

            return {
                mixer,
                actions,
                forward,
                reverse,
                setProgress
            }
        }

        /** 清理指定 AnimationMixer */
        _clearAnimationMixer = (mixer) => {
            if (!mixer) return

            mixer.stopAllAction()

            const index = this.#animationMixers.indexOf(mixer)
            if (index !== -1) {
                this.#animationMixers.splice(index, 1)
            }

            mixer.uncacheRoot(mixer.getRoot())
        }

        /** 应用transform */
        _applyTransform = (gltf, transform) => {
            if (!gltf || !transform) return
            const directions = ['x', 'y', 'z']
            const { position, rotation } = transform

            if (rotation) {
                directions.forEach(direc => {
                    const val = rotation[direc]
                    if (val !== undefined) {
                        gltf.scene.rotation[direc] += this._degToRad(val)
                    }
                })
            }

            if (position) {
                directions.forEach(direc => {
                    const val = position[direc]
                    if (val !== undefined) {
                        gltf.scene.position[direc] = val
                    }
                })
            }

        }

        /** 应用材质 */
        _applyMaterial = (gltf, material) => {
            if (!gltf || !material) return

            gltf.scene.traverse(node => {
                if (!node.isMesh) return
                const config = material[node.material.name]
                if (config) {
                    Object.keys(config).forEach(key => {
                        if (node.material[key]?.set) {
                            node.material[key].set(config[key])
                        } else {
                            node.material[key] = config[key]
                        }
                    })
                }
            })

            this._needRenderOnce = true
        }
    }

    class LyMainProductModelViewerBase extends LyMainProductModelViewerRenderConfigInitializer {
        _currentVariantId = null
        _currentLayout = null
        _currentModelConfig = null
        _currentYard = null

        _loadingCount = 0

        /** gltf缓存 */
        _gltfMap = {}

        /** 模型加载的任务id */
        _loadingIdMap = {}

        connectedCallback() {
            super.connectedCallback()

            this._currentVariantId = this.dataset.variantId
            this._currentLayout = this.dataset.layout

            this.spinner = this.nextElementSibling.querySelector('.loading__spinner')
        }

        disconnectedCallback() {
            super.disconnectedCallback()
        }

        _beforeVariantChange = ({ variantId, activedKey }) => {
            this._currentVariantId = variantId
            this._currentModelConfig = this.getVariantConfig(this._currentVariantId)
            this._currentYard = this._variantConfigs[this._currentVariantId].yard
            this._currentLayout = activedKey.split(' | ')[0]
        }

        /** 加载glb */
        loadGltf = async (url, type, key, transform, material) => {
            this.startLoading()

            const taskId = (this._loadingIdMap[key] ?? 0) + 1
            this._loadingIdMap[key] = taskId

            this.removeFromScene(key)

            let gltf = null

            try {
                this._gltfMap[key] = { url, type, transform, material }

                gltf = await this._loadGltf(url)

                const _taskId = this._loadingIdMap[key]
                if (_taskId !== taskId) {
                    const error = new Error(`[模型加载] ${type} 模型加载任务已被替代, 任务key: ${key}`)
                    error.code = 'TASK_INVALID'
                    throw error
                }

                const model = gltf.scene
                model.userData.__type__ = type
                model.userData.__key__ = key

                this._rootGroup.add(model)

                this._gltfMap[key].gltf = gltf

                this._applyTransform(gltf, transform)
                this._applyMaterial(gltf, material)

                return gltf
            } catch (error) {

                if (gltf && error.code === 'TASK_INVALID') {
                    this._disposeObject3D(gltf.scene)
                    delete this._gltfMap[key]
                }

                throw error
            } finally {
                this.stopLoading()
            }
        }

        /** 开始loading */
        startLoading = () => {
            this._loadingCount += 1
            this.updateSpinner()
        }

        /** 结束loading */
        stopLoading = () => {
            this._loadingCount -= 1
            this.updateSpinner()
        }

        /** 更改spinner的状态 */
        updateSpinner = () => this.spinner.classList.toggle('hidden', this._loadingCount <= 0)

        /** 根据模型的key从场景中移除对应的模型 */
        removeFromScene = (key) => {
            if (!this._rootGroup) return

            const targets = []

            this._rootGroup.children.forEach(child => {
                if (child.userData?.__key__ === key) {
                    targets.push(child)
                }
            })

            targets.forEach(model => {
                this._rootGroup.remove(model)

                this._disposeObject3D(model)

                const controller = model.userData.__animation_controller__
                if (controller) {
                    this._clearAnimationMixer(controller.mixer)
                }
            })

            const gltf = this._gltfMap[key]

            if (gltf?.parser) {
                gltf.parser.dispose?.()
            }

            delete this._gltfMap[key]
        }

        /** 清除场景模型 */
        clearScene = (whiteList = []) => {
            if (!this._rootGroup) return

            this._loadingIdMap = {}

            Object.keys(this._gltfMap).forEach(key => {
                if (whiteList.includes(key)) return
                this.removeFromScene(key)
            })
        }

        /** 获取模型配置的尺寸 */
        _getModelConfigSize(modelConfig) {
            if (!modelConfig?.model_size) return {}

            const { length, width, height } = modelConfig.model_size

            return {
                x: +length,
                y: +height,
                z: +width
            }
        }

        /** 叠加transform */
        _setTransform(resultTransform, transform, size = {}) {
            const { offset, rotation, post_offset_distance } = transform || {}

            if (offset) {
                Object.keys(offset).forEach(dir => {
                    if (!resultTransform.position[dir]) {
                        resultTransform.position[dir] = 0
                    }
                    resultTransform.position[dir] += offset[dir] * (size[dir] || 0)
                })
            }

            if (rotation) {
                Object.keys(rotation).forEach(dir => {
                    if (!resultTransform.rotation[dir]) {
                        resultTransform.rotation[dir] = 0
                    }
                    resultTransform.rotation[dir] += rotation[dir]
                })
            }

            if (post_offset_distance) {
                Object.keys(post_offset_distance).forEach(dir => {
                    if (!resultTransform.position[dir]) {
                        resultTransform.position[dir] = 0
                    }
                    resultTransform.position[dir] += post_offset_distance[dir]
                })
            }
        }

        /** 根据完整 handle 链路获取渲染配置 */
        getRenderConfig = (handleChain) => {
            if (!handleChain || !this._currentModelConfig) {
                return null
            }

            const handles = handleChain.split(',').map(h => h.trim()).filter(Boolean)

            if (!handles.length) {
                return null
            }

            const chain = []

            let currentModelConfig = this._currentModelConfig
            let index = 0

            // 普通款的链路是从模型开始的，所以先记录第一个节点为模型配置
            // 然后index从1开始，后续从连接点开始处理
            if (currentModelConfig.model) {
                chain.push({
                    type: 'modelConfig',
                    node: currentModelConfig
                })

                index = 1
            }

            // 遍历整条链路
            while (index < handles.length) {

                const linkPointHandle = handles[index]
                const modelConfigHandle = handles[index + 1]

                if (!linkPointHandle || !modelConfigHandle) {
                    return null
                }

                const linkPoint = currentModelConfig.link_points?.find(
                    lp => lp.handle === linkPointHandle
                )

                if (!linkPoint) {
                    console.warn(`未找到 LinkPoint: ${linkPointHandle}`)
                    return null
                }

                chain.push({
                    type: 'linkPoint',
                    node: linkPoint
                })

                const modelConfig = linkPoint.model_configs?.find(
                    mc => mc.handle === modelConfigHandle
                )

                if (!modelConfig) {
                    console.warn(`未找到 ModelConfig: ${modelConfigHandle}`)
                    return null
                }

                chain.push({
                    type: 'modelConfig',
                    node: modelConfig
                })

                currentModelConfig = modelConfig
                index += 2
            }

            if (!chain.length) {
                return null
            }

            const targetModelConfig = chain[chain.length - 1]?.node

            if (!targetModelConfig?.model) {
                return null
            }

            const result = {
                model: targetModelConfig.model,
                transform: {
                    position: {},
                    rotation: {}
                },
                materialConfig: targetModelConfig.material_config
            }

            /** transform 累积逻辑 */
            chain.forEach((curr, idx, arr) => {

                if (curr.type !== 'linkPoint') {
                    return
                }

                const transform = curr.node.transform

                let size = {}

                const prevNode = arr[idx - 1]?.node

                const nextNode = arr[idx + 1]?.node

                const prevSize = this._getModelConfigSize(prevNode)

                // join root 没有 model_size
                // fallback 到下一层 model
                const hasPrevSize = Object.keys(prevSize).length > 0

                size = hasPrevSize ? prevSize : this._getModelConfigSize(nextNode)

                this._setTransform(result.transform, transform, size)
            })

            return result
        }

        /** 根据 accessory sku id 获取所有可安装连接点 */
        getAccessoryLinkPoints = (accessoryId) => {

            if (!this._currentModelConfig) {
                return []
            }

            const result = []

            const walk = (modelConfig, currentChain = modelConfig.handle) => {

                if (!modelConfig) return

                const linkPoints = modelConfig.link_points || []

                linkPoints.forEach(linkPoint => {

                    const children = linkPoint.model_configs || []

                    children.forEach(child => {

                        const nextChain = `${currentChain},${linkPoint.handle},${child.handle}`

                        // accessory 命中
                        const matched = child.variants?.includes(+accessoryId)

                        if (matched) {
                            result.push({
                                handleChain: nextChain,
                                order: linkPoint.order ?? 0
                            })
                        }

                        // 继续向下递归
                        walk(child, nextChain)
                    })
                })
            }

            if (this._currentModelConfig.model) {
                walk(this._currentModelConfig)
            } else {
                this._currentModelConfig.link_points.forEach(linkPoint => {
                    const modelConfig = linkPoint.model_configs[0]
                    walk(modelConfig, `${linkPoint.handle},${modelConfig.handle}`)
                })
            }

            return result.sort((a, b) => a.order - b.order)
        }
    }

    customElements.define('ly-main-product-model-viewer', class extends LyMainProductModelViewerBase {

        _initBasePromise = Promise.resolve()

        pergolas = []
        pergolaKeys = []
        yardOffset = null

        /** 配件数量 */
        accessoryQuantityMap = {}

        /** 连接点占用状态 */
        accessoryOccupancyMap = {}

        /** 当前连接点安装的 sku */
        accessoryInstalledMap = {}

        /** 庭院模型遮挡检测 */
        #yardOcclusionMeshes = []
        #yardOcclusionRaycaster = new THREE.Raycaster()
        #yardOcclusionDirection = new THREE.Vector3()
        #yardOcclusionIntersections = []
        #yardMaterialStates = new Map()
        #isYardFaded = false
        #lastOcclusionCameraPosition = new THREE.Vector3(Infinity, Infinity, Infinity)
        #lastOcclusionTarget = new THREE.Vector3(Infinity, Infinity, Infinity)
        #lastOcclusionCheckTime = -Infinity
        #yardOcclusionGeneration = 0
        #yardOcclusionCheckInterval = 50
        #yardOcclusionOpacity = 0.15

        #sidewallEventKey = 'bus:sidewall_picker_changed'
        #sidewallAccessoryEventKey = 'bus:sidewall_accessory_picker_changed'
        #accessoryEventKey = 'bus:accessory_picker_changed'

        connectedCallback() {
            super.connectedCallback()

            this._initBasePromise = this._initBase().then(() => this._rootGroup.position.y = -1)

            this.shutterSwitcher = this.querySelector('.shutter-switcher>input')
            this.yardSwitcher = this.querySelector('.yard-switcher>input')

            this.handleShutterChange()
            if (!this.yardSwitcher.checked) {
                this.handleYardVisible()
            }

            this.shutterSwitcher.addEventListener('input', this.handleShutterChange)
            this.yardSwitcher.addEventListener('input', this.handleYardVisible)

            this.sidewallChangeUnsubscriber = window.subscribe(this.#sidewallEventKey, this.handleSidewallChange)
            this.sidewallAccessoryChangeUnsubscriber = window.subscribe(this.#sidewallAccessoryEventKey, this.handleSidewallAccessoryChange)
            this.accessoryChangeUnsubscriber = window.subscribe(this.#accessoryEventKey, this.handleAccessoryChange)
            this.pageTopChangeUnsubscriber = window.subscribe(
                'bus:main_product_model_viewer_page_top_changed',
                this.handlePageTopChange
            )
        }

        getCartPreviewData = async () => {
            const view = await this.getImageViewState()
            const models = Object.entries(this._gltfMap)
                .filter(([, item]) => item.type !== 'yard' && typeof item.url === 'string')
                .map(([key, item]) => {
                    const controller = item.gltf?.scene.userData.__animation_controller__
                    const action = controller?.actions?.['All Animations'] || Object.values(controller?.actions || {})[0]
                    const duration = action?.getClip().duration
                    const progress = Number.isFinite(duration) && duration > 0
                        ? THREE.MathUtils.clamp(action.time / duration, 0, 1)
                        : null
                    return {
                        key,
                        url: item.url,
                        type: item.type,
                        transform: item.transform || null,
                        materialConfig: item.material || null,
                        animation: progress === null ? null : {
                            name: action.getClip().name,
                            progress
                        }
                    }
                })
            if (!models.length) throw new Error('There is no model available for the cart preview.')
            return {
                state: {
                    schemaVersion: 1,
                    rootPosition: this._rootGroup.position.toArray(),
                    shutterOpen: Boolean(this.shutterSwitcher?.checked)
                },
                resources: {
                    schemaVersion: 1,
                    dracoDecoderPath: DRACO_DECODER_PATH,
                    models
                },
                view
            }
        }

        disconnectedCallback() {

            // 先停止渲染循环，防止后续异步操作
            this._stopRenderLoop()

            this._clearYardOcclusion()
            this.clearScene()

            this.shutterSwitcher?.removeEventListener('input', this.shutterChangeHandler)
            this.yardSwitcher?.removeEventListener('input', this.yardChangeHandler)

            this.sidewallChangeUnsubscriber?.()
            this.sidewallAccessoryChangeUnsubscriber?.()
            this.accessoryChangeUnsubscriber?.()
            this.pageTopChangeUnsubscriber?.()
            this.classList.remove('is-page-top')

            super.disconnectedCallback()
        }

        handlePageTopChange = (payload) => {
            const mediaScope = this.closest('.ly-main-product-media-wrapper')
            if (payload?.scope && payload.scope !== mediaScope) return

            const isMobile = window.matchMedia('(max-width: 767px)').matches
            this.classList.toggle('is-page-top', isMobile && Boolean(payload?.active))
        }

        _afterInitializedVariantConfigs = async () => {
            this._currentModelConfig = this.getVariantConfig(this._currentVariantId)
            this._currentYard = this._variantConfigs[this._currentVariantId].yard

            await this._initBasePromise

            this.initModels()
        }

        _onVariantChange = () => {
            this._clearYardOcclusion()
            this.clearScene()

            this.accessoryQuantityMap = {}
            this.accessoryOccupancyMap = {}
            this.accessoryInstalledMap = {}

            this.initModels()
        }

        initModels = async () => {
            if (!this._currentModelConfig) return

            this.initial()

            await Promise.all([
                ...this.loadPergolas(),
                this.loadYard()
            ])

            this.updateCamera()

            this._needRenderOnce = true
        }

        initial = () => {
            if (this._currentModelConfig.model) {
                this.pergolas = [this.resolvePergola(this._currentModelConfig)]
            } else {
                this.pergolas = this._currentModelConfig.link_points.map(linkPoint => {
                    return this.resolvePergola(linkPoint.model_configs[0], linkPoint)
                })
            }
        }

        resolvePergola = (modelConfig, linkPoint) => {
            const transform = { position: {}, rotation: {} }
            const size = this._getModelConfigSize(modelConfig)

            if (linkPoint) {
                this._setTransform(transform, linkPoint.transform, size)
            }

            if (modelConfig.yard_offset) {
                this.yardOffset = modelConfig.yard_offset
            }

            return {
                model: modelConfig.model,
                materialConfig: modelConfig.material_config,
                transform,
                size
            }
        }

        /** 加载凉棚模型 */
        loadPergolas = () => {
            this.pergolaKeys.length = 0
            return this.pergolas.map(async ({ model, transform, materialConfig }, index) => {
                const key = `pergola_${model}_${index}`
                this.pergolaKeys.push(key)
                const gltf = await this.loadGltf(model, 'pergola', key, transform, materialConfig)

                // 注册百叶窗动画
                gltf.scene.userData.__animation_controller__ = this._createAnimationController(gltf)

                return gltf
            })
        }

        /** 加载庭院模型 */
        loadYard = async () => {
            this._clearYardOcclusion()
            const occlusionGeneration = this.#yardOcclusionGeneration

            if (!this.yardSwitcher.checked) return
            const yard = this._currentYard
            const key = `yard_${yard}`
            const gltf = await this.loadGltf(yard, 'yard', key)

            if (this.yardOffset) {
                Object.entries(this.yardOffset).forEach(([direc, offset]) => {
                    gltf.scene.position[direc] += offset
                })
            }

            const isCurrentYard =
                occlusionGeneration === this.#yardOcclusionGeneration &&
                this.yardSwitcher.checked &&
                yard === this._currentYard &&
                this._gltfMap[key]?.gltf === gltf

            if (isCurrentYard) {
                this._registerYardOcclusion(gltf.scene)
            }

            return gltf
        }

        /** 缓存庭院模型中参与遮挡检测的可见网格 */
        _registerYardOcclusion = (yardModel) => {
            this._resetYardOcclusionState()
            yardModel.updateWorldMatrix(true, true)

            yardModel.traverseVisible(child => {
                if (!child.isMesh || !child.geometry) return

                if (child.geometry.boundingBox === null) {
                    child.geometry.computeBoundingBox()
                }
                if (child.geometry.boundingSphere === null) {
                    child.geometry.computeBoundingSphere()
                }

                const materials = Array.isArray(child.material)
                    ? child.material
                    : [child.material]

                if (!materials.some(material => material?.visible !== false && material.opacity > 0)) {
                    return
                }

                this.#yardOcclusionMeshes.push(child)

                materials.forEach(material => {
                    if (!material || this.#yardMaterialStates.has(material)) return

                    this.#yardMaterialStates.set(material, {
                        transparent: material.transparent,
                        opacity: material.opacity,
                        depthWrite: material.depthWrite
                    })
                })
            })
        }

        /** 根据遮挡状态统一设置或恢复整个庭院的透明度 */
        _setYardFaded = (isFaded) => {
            if (isFaded === this.#isYardFaded) return false

            this.#yardMaterialStates.forEach((state, material) => {
                const transparent = isFaded ? true : state.transparent
                const transparentChanged = material.transparent !== transparent

                material.transparent = transparent
                material.opacity = isFaded
                    ? Math.min(state.opacity, this.#yardOcclusionOpacity)
                    : state.opacity
                material.depthWrite = isFaded ? false : state.depthWrite

                if (transparentChanged) material.needsUpdate = true
            })

            this.#isYardFaded = isFaded
            return true
        }

        /** 重置庭院遮挡检测状态，但不使当前加载任务失效 */
        _resetYardOcclusionState = () => {
            this._setYardFaded(false)
            this.#yardOcclusionMeshes.length = 0
            this.#yardOcclusionIntersections.length = 0
            this.#yardMaterialStates.clear()
            this.#lastOcclusionCheckTime = -Infinity
            this.#lastOcclusionCameraPosition.set(Infinity, Infinity, Infinity)
            this.#lastOcclusionTarget.set(Infinity, Infinity, Infinity)
        }

        /** 清理庭院遮挡检测，并使尚未完成的庭院加载任务失效 */
        _clearYardOcclusion = () => {
            this.#yardOcclusionGeneration += 1
            this._resetYardOcclusionState()
        }

        /** 淡化位于相机和观察目标之间的庭院材质 */
        _updateYardOcclusion = () => {
            const controls = this._orbit
            if (!controls || !this.#yardOcclusionMeshes.length) return

            const camera = controls.object
            const target = controls.target
            const positionChanged = camera.position.distanceToSquared(this.#lastOcclusionCameraPosition) > 1e-8
            const targetChanged = target.distanceToSquared(this.#lastOcclusionTarget) > 1e-8

            if (!positionChanged && !targetChanged) return

            const now = performance.now()
            if (now - this.#lastOcclusionCheckTime < this.#yardOcclusionCheckInterval) return

            this.#lastOcclusionCheckTime = now
            this.#lastOcclusionCameraPosition.copy(camera.position)
            this.#lastOcclusionTarget.copy(target)

            const direction = this.#yardOcclusionDirection.copy(target).sub(camera.position)
            const targetDistance = direction.length()

            if (targetDistance <= 0.01) return

            direction.divideScalar(targetDistance)

            const raycaster = this.#yardOcclusionRaycaster
            raycaster.set(camera.position, direction)
            raycaster.near = 0.01
            raycaster.far = targetDistance - 0.01

            const intersections = this.#yardOcclusionIntersections
            intersections.length = 0
            for (const mesh of this.#yardOcclusionMeshes) {
                raycaster.intersectObject(mesh, false, intersections)
                if (intersections.length) break
            }

            if (this._setYardFaded(intersections.length > 0)) {
                this._needRenderOnce = true
            }
        }

        /** 渲染前更新庭院遮挡透明状态 */
        _beforeRender = () => {
            this._updateYardOcclusion()
        }

        get sizeValues() {
            let x = 0
            let z = 0
            this.pergolas.forEach(({ size }) => {
                x += size.x
                z += size.z
            })

            return [x, z]
        }

        /** 更改相机 */
        updateCamera = () => {
            const [sizeX, sizeZ] = this.sizeValues
            const angle = this._degToRad(20)
            const frontAngle = this._currentLayout === 'Wall-Mounted'
                ? this._degToRad(-45)
                : 0

            // 相机的垂直 FOV 为 20°，结合容器宽高比换算出横向视野。
            const { width, height } = this.getBoundingClientRect()
            const aspect = width > 0 && height > 0 ? width / height : 4 / 3
            const horizontalHalfFovTangent = Math.tan(this._degToRad(20) / 2) * aspect
            const framePadding = 1.2

            /** 根据指定视角下凉棚四角的横向投影，计算完整容纳模型所需的相机距离。 */
            const getCameraPosition = (viewAngle, maxDistance = 28) => {
                const directionX = Math.sin(viewAngle)
                const directionZ = Math.cos(viewAngle)
                const rightX = directionZ
                const rightZ = -directionX
                const halfX = sizeX / 2
                const halfZ = sizeZ / 2
                const corners = [
                    [halfX, halfZ],
                    [halfX, -halfZ],
                    [-halfX, halfZ],
                    [-halfX, -halfZ]
                ]

                const fitDistance = Math.max(...corners.map(([x, z]) => {
                    const depth = x * directionX + z * directionZ
                    const horizontalOffset = Math.abs(x * rightX + z * rightZ)

                    return depth + horizontalOffset / horizontalHalfFovTangent
                }))
                const distance = Math.min(fitDistance * framePadding, maxDistance)

                return new THREE.Vector3(
                    directionX * distance,
                    0,
                    directionZ * distance
                )
            }

            const frontPosition = getCameraPosition(frontAngle)
            // 左右交互视角允许使用 OrbitControls 的完整距离，避免超长凉棚的边墙超出画面。
            const leftPosition = getCameraPosition(frontAngle - angle, 100)
            const rightPosition = getCameraPosition(frontAngle + angle, 100)
            const backPosition = frontPosition.clone()
            backPosition.z *= -1

            const interactionCameraPositions = {
                front: frontPosition.toArray(),
                left: leftPosition.toArray(),
                right: rightPosition.toArray(),
                back: backPosition.toArray()
            }

            // addLinkPointCameraPositionCoordinate(interactionCameraPositions)

            const [targetX, targetY, targetZ] = interactionCameraPositions.front

            setTimeout(() => {
                this._moveCameraTo(targetX, targetY, targetZ + 1)
            }, 0)
        }

        /** 百叶窗交互 */
        handleShutterChange = () => {
            const controllers = this.pergolaKeys
                .map(key => this._gltfMap[key].gltf)
                .map(gltf => gltf.scene.userData.__animation_controller__)

            const action = this.shutterSwitcher.checked ? 'forward' : 'reverse'
            controllers.forEach(controller => {
                controller[action]?.()
            })
        }

        /** 庭院交互 */
        handleYardVisible = () => {
            if (!this.yardSwitcher.checked) {
                this._clearYardOcclusion()
                this.removeFromScene(`yard_${this._currentYard}`)
                return
            }
            this.loadYard()
        }

        /** sidewall选择器触发事件回调 */
        handleSidewallChange = async ({ checked, handleChain }) => {
            // console.log(handleChain)
            await Promise.all([this._readyPromise, this._initBasePromise])

            const key = handleChain.substring(0, handleChain.lastIndexOf(','))

            // 移除当前key对应的场景模型
            this.removeFromScene(key)

            if (checked) {
                const { model, transform, materialConfig } = this.getRenderConfig(handleChain)
                const gltf = await this.loadGltf(model, 'sidewall', key, transform, materialConfig)

                const target = this.getCameraPositionByHandleChain(handleChain)
                if (target) {
                    this._moveCameraTo(...target)
                }
            }
        }

        /** sidewall的配件选择器触发事件回调 */
        handleSidewallAccessoryChange = async ({ checked, handleChain }) => {
            // console.log(handleChain)
            await Promise.all([this._readyPromise, this._initBasePromise])

            const key = handleChain.substring(0, handleChain.lastIndexOf(','))

            // 移除当前key对应的场景模型
            this.removeFromScene(key)

            if (checked) {
                const { model, transform, materialConfig } = this.getRenderConfig(handleChain)
                const gltf = await this.loadGltf(model, 'sidewall_accessory', key, transform, materialConfig)

                const target = this.getCameraPositionByHandleChain(handleChain)
                if (target) {
                    this._moveCameraTo(...target)
                }
            }
        }

        /** accessory选择器触发事件回调 */
        handleAccessoryChange = async ({ id, quantity }) => {
            await Promise.all([this._readyPromise, this._initBasePromise])

            const linkPoints = this.getAccessoryLinkPoints(id)

            // 没用配置连接点，不做任何处理
            if (!linkPoints.length) {
                return
            }

            const lastQuantity = this.accessoryQuantityMap[id] ?? 0

            this.accessoryQuantityMap[id] = quantity

            // 数量增加
            if (quantity > lastQuantity) {
                const increase = quantity - lastQuantity
                const freePoints = linkPoints.filter(({ handleChain }) => !this.accessoryOccupancyMap[handleChain])

                const max = Math.min(increase, freePoints.length)
                const tasks = []

                for (let i = 0; i < max; i++) {

                    const task = (async () => {

                        const { handleChain } = freePoints[i]

                        const config = this.getRenderConfig(handleChain)

                        if (!config) {
                            return
                        }

                        const { model, transform, materialConfig } = config

                        // 提前占坑，避免并发竞争
                        this.accessoryOccupancyMap[handleChain] = true

                        this.accessoryInstalledMap[handleChain] = id

                        try {
                            const gltf = await this.loadGltf(model, 'accessory', handleChain, transform, materialConfig)

                            gltf.scene.userData.__sku__ = id

                        } catch (error) {
                            // 回滚
                            this.accessoryOccupancyMap[handleChain] = false

                            delete this.accessoryInstalledMap[handleChain]

                            throw error
                        }

                    })()
                    tasks.push(task)
                }

                await Promise.all(tasks)

                return
            }

            // 数量减少
            if (quantity < lastQuantity) {
                // 当前 sku 已安装的点
                const installedPoints = linkPoints.filter(({ handleChain }) => {
                    return this.accessoryInstalledMap[handleChain] === id
                }).reverse()

                if (quantity >= installedPoints.length) return

                const decrease = lastQuantity - quantity

                const max = Math.min(decrease, installedPoints.length)

                for (let i = 0; i < max; i++) {

                    const { handleChain } = installedPoints[i]

                    this.removeFromScene(handleChain)

                    this.accessoryOccupancyMap[handleChain] = false

                    delete this.accessoryInstalledMap[handleChain]
                }
            }
        }

        /** 根据handleChain获取对应的交互后的相机位置 */
        getCameraPositionByHandleChain = (handleChain) => {
            const position = this._cameraPositions.find(cp => {
                return cp.link_points.findIndex(hc => hc === handleChain) !== -1
            })

            if (!position || !position.coordinate) return null

            const { x_coordinate, y_coordinate, z_coordinate } = position.coordinate
            return [
                +x_coordinate,
                +y_coordinate,
                +z_coordinate
            ]
        }
    })
}
